import { useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, GraduationCap, Users, UserCircle, Moon, Sun, Sparkles, Shield } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';

const portals = [
  {
    id: 'admin',
    title: 'Admin Portal',
    description: 'School Administrators',
    icon: Building2,
    gradient: 'from-blue-500 to-indigo-600',
    hoverGradient: 'hover:from-blue-600 hover:to-indigo-700',
    path: '/auth/admin'
  },
  {
    id: 'staff',
    title: 'Staff Portal',
    description: 'Teachers & Staff',
    icon: GraduationCap,
    gradient: 'from-emerald-500 to-teal-600',
    hoverGradient: 'hover:from-emerald-600 hover:to-teal-700',
    path: '/auth/staff'
  },
  {
    id: 'student',
    title: 'Student Portal',
    description: 'Students',
    icon: Users,
    gradient: 'from-purple-500 to-pink-600',
    hoverGradient: 'hover:from-purple-600 hover:to-pink-700',
    path: '/auth/student'
  },
  {
    id: 'parent',
    title: 'Parent Portal',
    description: 'Parents & Guardians',
    icon: UserCircle,
    gradient: 'from-amber-500 to-orange-600',
    hoverGradient: 'hover:from-amber-600 hover:to-orange-700',
    path: '/auth/parent'
  },
  {
    id: 'super_admin',
    title: 'Super Admin',
    description: 'Platform Management',
    icon: Shield,
    gradient: 'from-red-500 to-rose-600',
    hoverGradient: 'hover:from-red-600 hover:to-rose-700',
    path: '/auth/admin'
  }
];

const PortalSelectorPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  // Redirect logged-in users to their dashboard
  useEffect(() => {
    if (user) {
      switch (user.role) {
        case 'super_admin':
          navigate('/super-admin', { replace: true });
          break;
        case 'school_admin':
          navigate('/admin', { replace: true });
          break;
        case 'teacher':
          navigate('/teacher', { replace: true });
          break;
        case 'student':
          navigate('/student', { replace: true });
          break;
        case 'parent':
          navigate('/parent', { replace: true });
          break;
        case 'staff':
          navigate('/staff-dashboard', { replace: true });
          break;
        default:
          break;
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        {/* Animated Orbs */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            x: [0, -30, 0],
            y: [0, 50, 0]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-3xl"
        />
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute top-6 right-6 z-20 text-white/70 hover:text-white hover:bg-white/10"
        data-testid="theme-toggle"
      >
        {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
      </Button>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-12"
        >
          {/* Logo */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-block mb-4 md:mb-6"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_school-hub-saas/artifacts/mh8ri9d5_CLOUD%20CAMPUS%20SCHOOLS.png" 
              alt="Cloud Campus Schools Logo"
              className="h-16 md:h-20 w-auto drop-shadow-2xl"
            />
          </motion.div>
          
          {/* Tagline */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-indigo-300/80 text-base md:text-lg mb-3 md:mb-4"
          >
            Modern School Management System
          </motion.p>
          
          {/* Title */}
          <motion.h1 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-2xl md:text-4xl font-bold text-white"
          >
            Select Your Portal
          </motion.h1>
        </motion.div>

        {/* Portal Cards - Fixed height for consistency */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 w-full max-w-5xl px-2"
        >
          {portals.map((portal, index) => {
            const Icon = portal.icon;
            return (
              <motion.div
                key={portal.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.03, y: -3 }}
                whileTap={{ scale: 0.98 }}
                className="h-full"
              >
                <Card 
                  className={`cursor-pointer bg-gradient-to-br ${portal.gradient} ${portal.hoverGradient} 
                    border-0 shadow-xl shadow-black/20 transition-all duration-300
                    hover:shadow-2xl hover:shadow-black/30 group overflow-hidden h-full`}
                  onClick={() => navigate(portal.path)}
                  data-testid={`portal-${portal.id}`}
                >
                  <CardContent className="p-4 md:p-8 text-center relative flex flex-col items-center justify-center min-h-[160px] md:min-h-[200px]">
                    {/* Shine Effect */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Icon */}
                    <motion.div 
                      className="w-12 h-12 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 rounded-xl md:rounded-2xl bg-white/20 backdrop-blur-sm 
                        flex items-center justify-center shadow-lg flex-shrink-0"
                      whileHover={{ rotate: [0, -10, 10, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <Icon className="h-6 w-6 md:h-10 md:w-10 text-white" />
                    </motion.div>
                    
                    {/* Title */}
                    <h3 className="text-base md:text-xl font-bold text-white mb-1">
                      {portal.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-white/70 text-xs md:text-sm">
                      {portal.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 md:mt-12 text-center px-4"
        >
          <p className="text-indigo-300/60 text-sm flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4" />
            Trusted by 500+ schools across India
          </p>
          
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mt-3 text-xs text-indigo-300/50">
            <Link to="/page/privacy-policy" className="hover:text-indigo-300 transition-colors">
              Privacy Policy
            </Link>
            <span>•</span>
            <Link to="/page/terms-of-use" className="hover:text-indigo-300 transition-colors">
              Terms of Use
            </Link>
            <span>•</span>
            <Link to="/page/refund-policy" className="hover:text-indigo-300 transition-colors">
              Refund Policy
            </Link>
          </div>
          
          <p className="text-indigo-300/40 text-xs mt-3">
            © {new Date().getFullYear()} Cloud Campus. Powered by{' '}
            <a href="https://www.ncriptech.com" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">ncriptech</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PortalSelectorPage;
